import Link from "next/link";
import Image, { StaticImageData } from "next/image";
import clsx from "clsx";
import React from "react";

type StaticImage = string | StaticImageData;

interface PrimaryBtnProps {
  text: React.ReactNode;
  iconSrc: StaticImage;
  dark?: boolean;
  isDisable?: boolean;
  styleClass?: string;
  href?: string;
  onClick?: () => void;
  size?: "default" | "small";
  noPill?: boolean;
  btnType?: "button" | "submit" | "reset";
}

export default function PrimaryBtn({
  text,
  iconSrc,
  dark = true,
  styleClass,
  onClick,
  href,
  size = "default",
  noPill = false,
  btnType = "button"
}: PrimaryBtnProps) {
  const isSmall = size === "small";

  const baseHeightClass = clsx(
    "h-[44px] sm:h-[50px]",
    !isSmall && "md:h-[var(--primarybtn-height)]"
  );

  const textSizeClass = clsx(
    "text-[14px] sm:text-[16px]",
    !isSmall && "md:text-[length:var(--primarybtn)] min-[2000px]:text-[19px]"
  );

  const paddingClass = clsx(
    noPill ? "px-8 sm:px-10" : "px-4 sm:px-5",
    !isSmall && !noPill && "md:px-10"
  );

  const iconWrapperClass = clsx(
    "w-[44px] h-[44px] sm:w-[50px] sm:h-[50px]",
    !isSmall && "md:w-[var(--primarybtn-height)] md:h-[var(--primarybtn-height)]"
  );

  const iconSize = isSmall ? 14 : 16;

  const content = (
    <>
      {/* Main Button Capsule */}
      <span
        className={clsx(
          baseHeightClass,
          paddingClass,
          textSizeClass,
          "rounded-full flex items-center justify-center font-semibold leading-[100%] whitespace-nowrap transition-colors",
          dark
            ? "text-white bg-white/5 border border-white/20 outline-white/10 hover:bg-white hover:text-[#00040b]"
            : "text-[#00040b] bg-white border border-[#ECF0F6] hover:bg-[#225BD4] hover:text-white"
        )}
      >
        {text}
      </span>

      {/* Render separate Icon Pill ONLY if noPill is false */}
      {!noPill && (
        <span
          className={clsx(
            iconWrapperClass,
            "shrink-0 rounded-full grid place-items-center transition-colors",
            dark ? "bg-white" : "bg-[#225BD4]"
          )}
        >
          {iconSrc ? (
            <Image
              src={iconSrc}
              alt="Arrow Icon"
              width={iconSize}
              height={iconSize}
              sizes="(max-width: 1024px) 100vw, 50vw"
              className="transition-transform duration-300 group-hover:rotate-[-90deg] w-[14px] h-[14px] sm:w-[16px] sm:h-[16px]"
            />
          ) : null}
        </span>
      )}
    </>
  );

  const commonClasses = clsx(
    "group inline-flex items-center select-none cursor-pointer py-5 lg:py-0",
    styleClass
  );

  if (href) {
    return (
      <Link href={href} onClick={onClick} className={commonClasses} style={{ width: "auto" }}>
        {content}
      </Link>
    );
  }

  return (
    <button onClick={onClick} className={commonClasses} style={{ width: "auto" }} type={btnType}>
      {content}
    </button>
  );
}